import { captureDesktopSyncOwner, captureDesktopObservationOwner, sameSyncOwner, retiredSyncResult, ownedSyncRead } from './desktopSyncOwnership';
import {
  replayCachedOrderFieldMutations,
  replayCachedOrderStatusMutations,
  syncHeartbeat,
  syncOrderSnapshot,
  syncRuns,
  syncShopIdentity,
  syncUserDirectory,
} from '@/api/desktopSync';
import { getActiveSessionRevision } from '@/api/authenticatedClient';
import { markReviewQueueDirty } from '@/utils/reviewQueueEvents';

export const DESKTOP_SYNC_STATUS_EVENT = 'lvb:desktop-sync-status';
export const LOCAL_STORE_RECOVERY_STATUS_EVENT = 'lvb:local-store-recovery-status';

const activeTasks = new Map();
const latestResults = new Map();
const latestTickets = new Map();
const resultOwners = new WeakMap();
let reportTicket = 0;
const pendingReports = new WeakMap();

const normalizedRoles = (roles) => Array.from(new Set(
  (Array.isArray(roles) ? roles : [])
    .filter((role) => typeof role === 'string')
    .map((role) => role.trim().toLowerCase())
    .filter(Boolean),
)).sort();

const hasRole = (context, allowed) => (
  normalizedRoles(context?.roles).some((role) => allowed.has(role))
);

export function buildDesktopSyncContext(user) {
  const context = {
    userId: typeof user?.id === 'string' ? user.id : null,
    roles: normalizedRoles(user?.roles),
    sessionRevision: getActiveSessionRevision(),
    tenant: user?.tenant_id || null,
  };
  return { ...context, owner: captureDesktopSyncOwner(context) };
}

function normalizeResult(result) {
  if (result && typeof result === 'object' && typeof result.state === 'string') return result;
  return { state: 'failed', reason: 'invalid_sync_result' };
}

function publishStatus(resource, result, owner, ticket) {
  const status = { ...normalizeResult(result), resource, completedAt: new Date().toISOString() };
  if (!owner.current() || latestTickets.get(resource) !== ticket) return { resource, ...retiredSyncResult() };
  latestResults.set(resource, { owner, status });
  resultOwners.set(status, { owner, resource, ticket });
  window.dispatchEvent(new CustomEvent(DESKTOP_SYNC_STATUS_EVENT, { detail: status }));
  return status;
}

function runResource(resource, task, owner) {
  if (!owner.current()) return Promise.resolve({ resource, ...retiredSyncResult() });
  const entries = activeTasks.get(resource) || new Set();
  const active = [...entries].find(entry => sameSyncOwner(entry.owner, owner));
  if (active) return active.observation;
  const ticket = {};
  latestTickets.set(resource, ticket);
  const entry = { owner };
  entries.add(entry); activeTasks.set(resource, entries);
  let timer;
  const work = Promise.resolve().then(() => owner.current() ? task() : retiredSyncResult())
    .then(result => publishStatus(resource, result, owner, ticket))
    .catch(error => publishStatus(resource, { state: 'failed', reason: error?.message || 'unknown' }, owner, ticket))
    .finally(() => { entries.delete(entry); if (!entries.size) activeTasks.delete(resource); });
  // Stop waiting without releasing an unsettled task or claiming it was cancelled.
  entry.observation = Promise.race([work, new Promise(resolve => {
    timer = setTimeout(() => resolve(publishStatus(resource,
      { state: 'pending', reason: 'sync_settlement_pending' }, owner, ticket)), 31000);
  })]).finally(() => clearTimeout(timer));
  return entry.observation;
}

async function reportRestoration(results, owner) {
  const reporter = owner.bridge?.reportLocalStoreRestoration;
  const current = result => {
    const evidence = resultOwners.get(result);
    return evidence && evidence.owner.current() && latestTickets.get(evidence.resource) === evidence.ticket;
  };
  const accepted = results.filter(current);
  if (!owner.current() || !accepted.length || typeof reporter !== 'function') return null;
  if (pendingReports.has(owner.bridge)) return null;
  const claim = {};
  const ticket = ++reportTicket;
  pendingReports.set(owner.bridge, claim);
  let timer;
  const work = Promise.resolve().then(async () => {
    if (!owner.current() || !accepted.every(current)) return null;
    const status = await reporter.call(owner.bridge, accepted);
    if (ticket === reportTicket && owner.current() && accepted.every(current)
        && owner.bridge.reportLocalStoreRestoration === reporter
        && status && typeof status.state === 'string') {
      window.dispatchEvent(new CustomEvent(LOCAL_STORE_RECOVERY_STATUS_EVENT, { detail: status }));
    }
    return status;
  }).catch(() => null).finally(() => {
    if (pendingReports.get(owner.bridge) === claim) pendingReports.delete(owner.bridge);
  });
  // Stop awaiting a lost IPC reply, but do not overlap it with another report.
  return Promise.race([work, new Promise(resolve => { timer = setTimeout(() => resolve(null), 30000); })])
    .finally(() => clearTimeout(timer));
}

async function replayOrderChanges(context) {
  const [statusResult, fieldResult] = await Promise.all([
    replayCachedOrderStatusMutations(context),
    replayCachedOrderFieldMutations(context),
  ]);
  if (statusResult?.reviewMayHaveChanged || fieldResult?.reviewMayHaveChanged) {
    markReviewQueueDirty('desktop_replay_completed');
  }
  return {
    state: 'completed',
    claimed: (statusResult?.claimed || 0) + (fieldResult?.claimed || 0),
    acknowledgementFailures:
      (statusResult?.acknowledgementFailures || 0)
      + (fieldResult?.acknowledgementFailures || 0),
  };
}

export async function runDesktopSyncBatch(context = buildDesktopSyncContext(null)) {
  // Refresh device authority before attempting replay. A secondary desktop may
  // continue refreshing its read-only caches, but the local replay endpoints
  // deliberately require the registered primary and otherwise answer 403.
  const owner = captureDesktopSyncOwner(context);
  if (!owner.current()) return [];
  const capturedContext = { ...context, owner };
  const heartbeat = await runResource('heartbeat', syncHeartbeat, owner);
  if (!owner.current()) return [heartbeat];
  const mayReplay = heartbeat?.state === 'completed'
    && heartbeat?.status?.registered
    && heartbeat?.status?.isPrimary
    && hasRole(context, new Set(['admin', 'designer']));
  const maySyncRuns = hasRole(context, new Set(['admin']));
  const tasks = {
    replay: mayReplay
      ? () => replayOrderChanges(capturedContext)
      : () => ({
        state: 'skipped',
        reason: hasRole(context, new Set(['admin', 'designer']))
          ? 'primary_desktop_required'
          : 'replay_role_required',
      }),
    orders: () => ownedSyncRead(owner, () => syncOrderSnapshot()),
    shop: () => syncShopIdentity(capturedContext),
    runs: maySyncRuns
      ? () => syncRuns(capturedContext)
      : () => ({ state: 'skipped', reason: 'routes_read_required' }),
    users: () => syncUserDirectory(capturedContext),
  };
  const results = [
    heartbeat,
    ...await Promise.all(
      Object.entries(tasks).map(([resource, task]) => runResource(resource, task, owner)),
    ),
  ];
  await reportRestoration(results, owner);
  return results;
}

export async function reportOrderDataStatus(status, context) {
  const owner = context ? captureDesktopSyncOwner(context) : captureDesktopObservationOwner();
  if (!owner.current()) return retiredSyncResult();
  const ticket = {};
  latestTickets.set('orders', ticket);
  let result;
  try { result = status ? await syncOrderSnapshot(status) : await ownedSyncRead(owner, () => syncOrderSnapshot()); }
  catch { result = { state: 'failed', reason: 'order_data_status_unavailable' }; }
  const published = publishStatus('orders', result, owner, ticket);
  await reportRestoration([published], owner);
  return published;
}

export function getDesktopSyncStatus() {
  return Object.fromEntries([...latestResults].filter(([, entry]) => entry.owner.current())
    .map(([resource, entry]) => [resource, entry.status]));
}

export function resetDesktopSyncCoordinatorForTests() {
  activeTasks.clear();
  latestResults.clear();
  latestTickets.clear();
  reportTicket++;
}
